import type { ClassDefinition } from '../../schemas/classDefinition';
import { warlockAdvanced2to5Moves } from '../moves/warlock/advanced-2-5';
import { warlockAdvanced6to10Moves } from '../moves/warlock/advanced-6-10';
import { warlockStartingAutoMoves, warlockStartingChoiceMoves } from '../moves/warlock/initial';

/**
 * Definizione della classe **Warlock** (manuale V1, rr. 5768-6177).
 *
 * Undicesima classe trascritta. **5ª e ultima classe casteggiante DWR**.
 *
 * Pattern principali stress-testati:
 *
 * - **Casteggiante condizionata a *Vincolo del Tomo* (D2)**: pattern già consolidato dal
 *   Druido (*Divinità della Natura*) e dal Paladino (*Ordine Sacro*). La mossa di scelta
 *   `warlock.vincolo-del-tomo` È il trigger semantico per `getActiveSpellLists`. *Mana* e
 *   *Lanciare Incantesimi del Patrono* sono regole description-only dentro il testo del
 *   *Vincolo del Tomo*, non Move separate nel catalogo.
 *
 * - **`SpellcastingConfig` con `cantripLabel: 'Suppliche'`**, `castingMoveId:
 *   'warlock.vincolo-del-tomo'`, `manaMoveId: 'warlock.mana'` (segnaposto semantico, nessuna
 *   Move in catalogo — identico al pattern Druido che ha `manaMoveId: 'druido.mana'`).
 *   `tradizioni: []` (no Tradizioni per il Warlock, una sola lista del Patrono).
 *
 * - **FI-27 chiusura strutturale** (D3): la mossa `paladino.ordine-sovrannaturale` viene
 *   refattorata con `MoveSchema.choices` [3 Vincoli], `choiceCount: 1`. La spell-list
 *   `warlock-native` si attiva sul pattern Paladino-Sovrannaturale +
 *   `selectedChoices=['vincolo-del-tomo']` (gestito in `services/spellAccess.ts`).
 *
 * **Punti notevoli**:
 * - Dado di danno **d6**, PF base **6** (= Mago), Carico base **7**
 * - 5 archetipi razza-tematici
 * - 3 mosse iniziali automatiche + 3 mosse iniziali di scelta (3 Vincoli, `pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (41 mosse totali)
 * - 4 template di Legami (= `maxBonds`, FI-3 rispettato)
 * - 4 categorie di Aspetto (Occhi/Capelli/Vesti/Corpo)
 *
 * **D5 — Archetipo Warlock-Nano**: ottiene «immunità ai veleni» gratuita dalle 12
 * opzioni del *Contratto*. Effetto narrativo runtime, description-only nel testo
 * dell'archetipo Nano + nel testo di *Contratto*. Niente `extraTags` schema.
 */
export const warlockClassDefinition: ClassDefinition = {
  id: 'warlock',
  name: 'Warlock',

  intro:
    "La vita è un susseguirsi di porte chiuse, di limiti che non puoi superare, di poteri che non ti appartengono. Ti sei dibattuto, hai provato a forzare la serratura con la tua sola forza, con il tuo ingegno, con la tua stessa disperazione.\n\nLa risposta è arrivata solo quando hai smesso di lottare, quando hai sentito quella voce, un sussurro che ti offriva una via d'uscita, una promessa che non osavi nemmeno sognare. Non è stato un atto di empietà, ma un contratto. Un patto sigillato nell'oscurità, che ha trasformato la tua debolezza in una forza che scorre nelle tue vene come fuoco. L'anima… parole vuote, promesse vane. Che te la mostrino, se ne sono capaci. Che ti dicano qual è il suo valore. Hai barattato un'illusione per ciò che brami di più: un'arma che non può essere spezzata, un libro che svela i segreti dell'universo, o catene plasmate per legare a te le anime altrui.\n\nE ora che il potere è tuo, che puoi piegare la volontà altrui, riscrivere le leggi della magia o forgiare il tuo destino, il mondo ti giudicherà. Ti chiameranno mostro, ti vedranno come un pericolo. Ma ogni volta che la tua mano sinistra brucia con la magia che non ti appartiene, ogni volta che il tuo servitore ultraterreno ti segue fedelmente, saprai che il loro giudizio non conta nulla. Il patto era solo l'inizio di una storia che scriverai tu stesso, a dispetto di tutto. Perché, in fondo, l'unica porta che conta è quella che si è aperta davanti a te.",

  // Manuale V1, rr. 5777-5783
  suggestedNames: [
    // Umano
    'Oryuk',
    'Eva',
    'Faustus',
    'Elric',
    'Semundur',
    'Solomon',
    'Michael',
    'Piper',
    'Willow',
    'Mab',
    'Zarya',
    // Elfo
    'Cymoril',
    'Aspell',
    'Kratim',
    'Emander',
    'Latim',
    'Danfal',
    'Lantar',
    'Sausa',
    'Edel',
    'Nuswil',
    // Nano
    'Thothtan',
    'Ragan',
    'Lagar',
    'Zida',
    'Timde',
    'Krasa',
    'Dangi',
    'Ror',
    'Karda',
    'Laika',
    'Nud',
    // Halfling
    'Masa',
    'Sole',
    'Riga',
    'Giono',
    'Stranul',
    'Dalfmel',
    'Proszo',
    'Neastran',
    'Luna',
    'Emasa',
    // Mezz'orco
    'Viscul',
    'Lusna',
    'Swama',
    'Monra',
    'Oru',
    'Doomrar',
    "Vol'mer",
    'Guska',
    'Sa',
    'Gaka',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 5785-5791
  aspetti: [
    {
      label: 'Occhi',
      options: ['Folli', 'Tormentati', 'Scaltri'],
    },
    {
      label: 'Capelli',
      options: ['Capigliatura Impeccabile', 'Capelli Lunghissimi', 'Calvo'],
    },
    {
      label: 'Vesti',
      options: ['Abiti Comuni', 'Abiti Scuri', 'Abiti Eleganti'],
    },
    {
      label: 'Corpo',
      options: ['Allenato', 'Coperto di Cicatrici', 'Magro'],
    },
  ],

  // Manuale V1, rr. 5793-5799
  damageDie: 'd6',
  baseHp: 6,
  baseLoad: 7,

  // Manuale V1, rr. 5801-5809
  alignmentPrecepts: {
    buono: 'Intercedi in favore di qualcuno con il tuo patrono.',
    legale: 'Ti metti in pericolo per compiere la volontà del tuo patrono.',
    neutrale: 'Accresci il tuo potere senza causare danni a nessun altro.',
    caotico: 'Sfidi il tuo patrono pur di affermare te stesso.',
    malvagio: 'Corrompi qualcun altro come sei stato corrotto tu.',
  },

  // Manuale V1, rr. 5828-5834. **maxBonds: 4** (4 templates ↔ 4, FI-3 rispettato).
  bondTemplates: [
    'Devo nascondere la mia vera natura a _____.',
    '_____ sarebbe un eccellente servitore per il mio padrone.',
    'Solo _____ potrebbe salvare la mia anima. Ma lo voglio davvero?',
    'So cosa desidera veramente _____. Come potrò trarne vantaggio?',
  ],
  maxBonds: 4,

  // Manuale V1, rr. 5836-5840
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        'Quando usi un credito ottenuto con *Vincolo della Catena* come leva per *parlamentare* con un PNG, puoi trovare una scusa per non considerare saldato il suo debito (vedi mossa archetipo).',
      grantedMoveId: 'warlock.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Aggiungi un trucchetto dalla lista del Mago alle suppliche concesse con il *Vincolo del Tomo*, e quando prendi *Le Vie del Patrono...* puoi scegliere anche tra le mosse del Mago.',
      grantedMoveId: 'warlock.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        'Ottieni «*immunità ai veleni*» oltre ai benefici concessi dal tuo *Contratto* (D5: gratis tra le 12 opzioni).',
      grantedMoveId: 'warlock.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        'Quando usi *La Fortuna degli Sciocchi*, prendi +1 a qualsiasi prossimo tiro (non solo *sfidare il pericolo*).',
      grantedMoveId: 'warlock.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description:
        "L'arma del *Vincolo della Lama* ha di base il miglioramento «*truce*», e quando prendi *Le Vie del Patrono...* puoi scegliere anche tra le mosse del Guerriero.",
      grantedMoveId: 'warlock.archetipo-mezzorco',
    },
  ],

  startingMoves: warlockStartingAutoMoves.map((m) => m.id),

  // Manuale V1, r. 5899: "*Inoltre, scegli un Vincolo:*"
  startingChoiceGroups: [
    {
      id: 'vincolo-del-patrono',
      name: 'Vincolo del Patrono',
      description:
        'Scegli uno dei 3 Vincoli concessi dal tuo Patrono. *Vincolo della Catena* (debiti come leva di *parlamentare*), *Vincolo del Tomo* (Tomo + suppliche + 2 spell L1: trigger del casting `warlock-native`), *Vincolo della Lama* (arma ultraterrena con miglioramenti).',
      moveOptions: warlockStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: warlockAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: warlockAdvanced6to10Moves.map((m) => m.id),

  // **5ª casteggiante DWR**: cantripLabel 'Suppliche', niente Tradizioni.
  // Trigger semantico del casting = la mossa di scelta `warlock.vincolo-del-tomo`
  // (pattern Druido/Paladino, vedi D2). *Mana* è segnaposto semantico (no Move in
  // catalogo, identico a `druido.mana`).
  spellcasting: {
    cantripLabel: 'Suppliche',
    castingMoveId: 'warlock.vincolo-del-tomo',
    manaMoveId: 'warlock.mana',
    tradizioni: [],
  },

  // Manuale V1, rr. 5812-5826
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
      {
        id: 'copia-del-contratto',
        name: 'Copia del Contratto',
        weight: 0,
        tags: [],
        description:
          'Una copia del *Contratto* firmato col patrono. Descrivila col GM (forma, materiale, sigillo, postille).',
      },
    ],
    choices: [
      {
        id: 'arma-iniziale',
        prompt: "Scegli un'opzione per le tue armi",
        pickCount: 1,
        options: [
          {
            id: 'spada-corta',
            label: 'Spada corta',
            items: [
              {
                id: 'spada-corta',
                name: 'Spada corta',
                weight: 1,
                tags: ['media'],
              },
            ],
          },
          {
            id: 'arco-logoro-faretra-pugnale',
            label: 'Arco logoro + Faretra di frecce + Pugnale',
            items: [
              {
                id: 'arco-logoro',
                name: 'Arco logoro',
                weight: 2,
                tags: ['vicino', 'a due mani'],
              },
              {
                id: 'faretra-di-frecce',
                name: 'Faretra di frecce',
                weight: 1,
                uses: 3,
                tags: [],
                description: '3 munizioni.',
              },
              {
                id: 'pugnale',
                name: 'Pugnale',
                weight: 1,
                tags: ['corta'],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'pozione-curativa',
            label: 'Pozione Curativa',
            items: [
              {
                id: 'pozione-curativa',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
          {
            id: 'antitossina',
            label: 'Antitossina',
            items: [
              {
                id: 'antitossina',
                name: 'Antitossina',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
          {
            id: 'monete',
            label: '17 monete',
            items: [
              {
                id: 'monete',
                name: 'Monete',
                weight: 0,
                quantity: 17,
                tags: [],
                description: 'Soldi del PG.',
              },
            ],
          },
        ],
      },
    ],
  },
};
